import { Patient, Resource } from '@medplum/fhirtypes'
import { Button, Group, Modal, Stack } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconPlus } from '@tabler/icons-react'
import { ReactNode, useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { useQueryClient } from '@tanstack/react-query'
import { useScrollContext } from '@/context/ScrollContext'
import { logError } from '@/utils/errorUtils'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'

type AddResourceSectionProps<TResource extends Resource> = {
  patient: Patient
  title: string
  modalTitle: string
  modalSize?: string | number
  /** Render-prop for the modal body. */
  renderForm: (ctx: { submitting: boolean }) => ReactNode
  /** Build the FHIR resource on submit, or return a validation error message. */
  buildResource: () => TResource | { error: string }
  /** FHIR resource type for `medplum.invalidateSearches`. */
  resourceType: TResource['resourceType']
  /** Disable the Create button (form validation state). */
  isSubmitDisabled?: boolean
  /** Reset form state between submissions/cancels. */
  resetForm: () => void
  /** Sentry feature tag context. */
  featureContext: string
  /** Display label for success/error toasts (e.g. "diagnosis", "allergy"). */
  noun: string
  /** Children render the list below the header — typically <ResourceTable />. Receives the refresh key. */
  children: (refreshKey: number) => ReactNode
  /** Override the scroll-to-top behavior (Allergies uses window.scrollTo). */
  scrollMode?: 'context' | 'window' | 'none'
}

export function AddResourceSection<TResource extends Resource>({
  patient,
  title,
  modalTitle,
  modalSize = 'lg',
  renderForm,
  buildResource,
  resourceType,
  isSubmitDisabled,
  resetForm,
  featureContext,
  noun,
  children,
  scrollMode = 'context',
}: AddResourceSectionProps<TResource>) {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const { scrollToTop } = useScrollContext()

  const [isOpen, setIsOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (scrollMode === 'context') scrollToTop()
    else if (scrollMode === 'window') window.scrollTo({ top: 0, behavior: 'smooth' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    if (submitting) return
    setIsOpen(false)
    resetForm()
  }

  const handleSubmit = async () => {
    if (!patient.id) return
    const built = buildResource()
    if ('error' in built) {
      showNotification({
        title: 'Missing required fields',
        // TS can't narrow `TResource | { error: string }` through the generic;
        // the error branch is always the `{ error: string }` shape at runtime.
        message: built.error as string,
        color: 'red',
      })
      return
    }

    setSubmitting(true)
    try {
      await medplum.createResource(built)
      medplum.invalidateSearches(resourceType)
      await queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patient.id) })

      showNotification({
        title: `${noun.charAt(0).toUpperCase()}${noun.slice(1)} added`,
        message: `The ${noun} was created successfully.`,
        color: 'green',
      })

      setIsOpen(false)
      resetForm()
      setRefreshKey((k) => k + 1)
    } catch (e) {
      logError('patient', `Failed to create ${featureContext}`, e)
      showNotification({
        title: 'Error',
        message: `Failed to create ${noun}.`,
        color: 'red',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center gap-4">
        <p className="text-2xl font-bold">{title}</p>
        <Button
          variant="outline"
          className="!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] !p-2 hover:from-[#FFFFFF]"
          onClick={() => setIsOpen(true)}
        >
          Add <IconPlus width={15} className="ml-1" />
        </Button>
      </div>

      <Modal opened={isOpen} onClose={handleClose} title={modalTitle} size={modalSize} centered>
        <Stack gap="sm">
          {renderForm({ submitting })}
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting} disabled={isSubmitDisabled}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {children(refreshKey)}
    </div>
  )
}
